import React, { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import BasicImage from '@home/components/BasicImage';
import EllipsisHover from '@gp/components/EllipsisHover';
import agentInstructions from '@/pages/aigc/assets/svg/agent-Instructions.svg';
import agentCloseIcon from '@/pages/aigc/assets/svg/agent-clone.svg';

export interface ICopilotRefTagChipProps {
  /** 外部传入的前缀文案（空则整块不展示） */
  text?: string;
  /** 会话切换时重置「已关闭」状态 */
  sessionId?: string;
  /** 是否展示关闭按钮（仅 opinion_pc 为 true） */
  showClose?: boolean;
  /** 是否隐藏左侧箭头图标 */
  hideArrow?: boolean;
  /** 左侧缩略图 URL（如 prefixImage） */
  imgSrc?: string;
  /** 关闭 chip 时通知父级清除外部引用参数 */
  onDismiss?: () => void;
}

/**
 * Copilot 输入区外部引用前缀 chip。
 * 布局：[箭头?] [缩略图?] [文案] [关闭?]
 */
const CopilotRefTagChip: React.FC<ICopilotRefTagChipProps> = ({
  text,
  sessionId,
  showClose = false,
  hideArrow = false,
  imgSrc,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [text, sessionId]);

  const hasText = text !== undefined && text !== '';
  const visible = hasText && !dismissed;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setDismissed(true);
    onDismiss?.();
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="dba-qa-input-copilot-ref">
      {!hideArrow && (
        <span className="dba-qa-input-copilot-ref__arrow" aria-hidden>
          <BasicImage
            src={agentInstructions}
            alt=""
            className="dba-qa-input-copilot-ref__arrow-img"
            errorSrc={agentInstructions}
          />
        </span>
      )}
      <div
        className={classNames('dba-qa-input-copilot-ref__chip', {
          'dba-qa-input-copilot-ref__chip--closable': showClose,
        })}
        contentEditable={false}
        suppressContentEditableWarning
        onMouseDown={handleMouseDown}
      >
        {imgSrc && (
          <span className="dba-qa-input-copilot-ref__thumb">
            <BasicImage
              src={imgSrc}
              alt=""
              className="dba-qa-input-copilot-ref__thumb-img"
            />
          </span>
        )}
        <EllipsisHover text={`「${text}」`} className="dba-qa-input-copilot-ref__text" />
        {showClose && (
          <button
            type="button"
            className="dba-qa-input-copilot-ref__close"
            onClick={handleClose}
            aria-label="close"
          >
            <BasicImage
              src={agentCloseIcon}
              alt="close"
              className="dba-qa-input-copilot-ref__close-icon"
              errorSrc={agentCloseIcon}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default memo(CopilotRefTagChip);
